import {
  planLegacyImportFromUnknown,
  type LegacyImportPlan,
} from "@haulsheet/core";
import { apiFetch, ApiClientError } from "../api/client";

/** localStorage keys the old single-file Haul Sheet / Prebook Brief wrote (blueprint §12). */
export const LEGACY_STORAGE_KEYS = [
  "haulSheetLoadLog_v1",
  "haulSheetFuelLog",
  "haulSheetDetentionLog",
  "haulSheetMaintLog",
  "haulSheetBreakEven",
  "haulSheetRunRates",
  "haulSheetCountExpenses",
  "haulSheetDismissed",
  "haulSheetDetentionCfg",
  "prebookBriefCutPct",
  "prebookBriefBennett",
  "prebookBriefLoadLog",
] as const;

export type LegacyBundleInput = Record<string, unknown>;

export type ParseResult =
  | { ok: true; bundle: LegacyBundleInput; keys: string[]; notice: string | null }
  | { ok: false; error: string };

const KEY_SET = new Set<string>(LEGACY_STORAGE_KEYS);

/** Read every legacy key present in this browser's storage. */
export function readLegacyStorage(storage: Pick<Storage, "getItem">): {
  bundle: LegacyBundleInput;
  keys: string[];
} {
  const bundle: LegacyBundleInput = {};
  const keys: string[] = [];
  for (const k of LEGACY_STORAGE_KEYS) {
    let v: string | null = null;
    try {
      v = storage.getItem(k);
    } catch {
      v = null;
    }
    if (v == null) continue;
    keys.push(k);
    try {
      bundle[k] = JSON.parse(v);
    } catch {
      bundle[k] = v;
    }
  }
  return { bundle, keys };
}

/**
 * Validate pasted text. Accepts the key map ({ haulSheetLoadLog_v1: [...], ... }),
 * a { bundle: {...} } wrapper, or a bare load-log array.
 */
export function parseLegacyInput(text: string): ParseResult {
  const trimmed = text.trim();
  if (!trimmed) {
    return {
      ok: false,
      error: "Nothing to import yet. Tap “Read this browser” or paste your old data.",
    };
  }
  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    return {
      ok: false,
      error: `That isn’t valid JSON (${detail}). Paste the whole export, starting with “{”.`,
    };
  }
  if (Array.isArray(raw)) {
    const looksLikeLoads = raw.every((r) => r && typeof r === "object" && !Array.isArray(r));
    if (!looksLikeLoads || raw.length === 0) {
      return { ok: false, error: "That list is empty or isn’t a load log." };
    }
    return {
      ok: true,
      bundle: { haulSheetLoadLog_v1: raw },
      keys: ["haulSheetLoadLog_v1"],
      notice: "Read a bare list as your load log.",
    };
  }
  if (!raw || typeof raw !== "object") {
    return { ok: false, error: "Expected an object of old Haul Sheet keys, not a single value." };
  }
  let obj = raw as Record<string, unknown>;
  if (
    obj.bundle &&
    typeof obj.bundle === "object" &&
    !Array.isArray(obj.bundle) &&
    !Object.keys(obj).some((k) => KEY_SET.has(k))
  ) {
    obj = obj.bundle as Record<string, unknown>;
  }
  const keys = Object.keys(obj).filter((k) => KEY_SET.has(k));
  if (keys.length === 0) {
    return {
      ok: false,
      error:
        "No old Haul Sheet data found in that JSON. Expected keys like haulSheetLoadLog_v1, haulSheetFuelLog or prebookBriefLoadLog.",
    };
  }
  const bundle: LegacyBundleInput = {};
  for (const k of keys) bundle[k] = obj[k];
  const ignored = Object.keys(obj).length - keys.length;
  return {
    ok: true,
    bundle,
    keys,
    notice: ignored > 0 ? `Ignored ${ignored} unrelated key${ignored === 1 ? "" : "s"}.` : null,
  };
}

/**
 * The server only needs to know a fuel row HAD a photo (it records a receipt
 * placeholder); the base64 itself can be megabytes and blow the request limit.
 * Keep the data-URL header, drop the payload.
 */
export function slimBundleForUpload(bundle: LegacyBundleInput): LegacyBundleInput {
  const fuel = bundle.haulSheetFuelLog;
  if (!Array.isArray(fuel)) return bundle;
  return {
    ...bundle,
    haulSheetFuelLog: fuel.map((r) => {
      if (!r || typeof r !== "object") return r;
      const photo = (r as { photo?: unknown }).photo;
      if (typeof photo !== "string" || !photo.startsWith("data:")) return r;
      const comma = photo.indexOf(",");
      return { ...(r as object), photo: comma >= 0 ? photo.slice(0, comma + 1) : "data:" };
    }),
  };
}

export type ImportCounts = LegacyImportPlan["counts"];
export type ImportTotals = LegacyImportPlan["totals"];

export interface ImportExisting {
  loads: number;
  brokers: number;
  fuel: number;
  maintenance: number;
  detentions: number;
  contracts: number;
}

export interface ImportCreated {
  loads: number;
  brokers: number;
  /** fuel + maintenance rows written */
  expenses: number;
  detentions: number;
  contracts: number;
  documents: number;
  /** Server retired the org's unedited 0% "Default lease" (import contracts take over). */
  defaultContractRetired?: boolean;
}

export interface LegacyImportResponse {
  preview: boolean;
  counts: ImportCounts;
  totals: ImportTotals;
  created: ImportCreated;
  existing: ImportExisting | null;
}

export interface LocalPreview {
  counts: ImportCounts;
  totals: ImportTotals;
  settings: { costs: boolean; mpg: boolean; detention: boolean };
}

/** Same pure planner the server runs; used for instant counts and settings flags. */
export function planLocally(bundle: LegacyBundleInput): LocalPreview {
  const plan = planLegacyImportFromUnknown(bundle);
  return {
    counts: plan.counts,
    totals: plan.totals,
    settings: {
      costs: plan.costProfile != null,
      mpg: plan.truck?.mpg != null,
      detention: plan.detentionCfg != null,
    },
  };
}

/** Records the user would recognize (contracts are bookkeeping, not counted here). */
export function recordCount(c: ImportCounts): number {
  return c.loads + c.fuel + c.maintenance + c.detention;
}

export function hasAnythingToImport(p: LocalPreview): boolean {
  return (
    recordCount(p.counts) > 0 || p.settings.costs || p.settings.mpg || p.settings.detention
  );
}

export interface CountRow {
  key: string;
  label: string;
  count: number;
  existing: number | null;
}

/** Rows for the preview table, with "already there" from the server preview. */
export function previewRows(
  counts: ImportCounts,
  existing: ImportExisting | null,
): CountRow[] {
  return [
    { key: "loads", label: "Loads", count: counts.loads, existing: existing?.loads ?? null },
    { key: "brokers", label: "Brokers", count: counts.brokers, existing: existing?.brokers ?? null },
    { key: "fuel", label: "Fuel fill-ups", count: counts.fuel, existing: existing?.fuel ?? null },
    {
      key: "maintenance",
      label: "Maintenance",
      count: counts.maintenance,
      existing: existing?.maintenance ?? null,
    },
    {
      key: "detention",
      label: "Detention",
      count: counts.detention,
      existing: existing?.detentions ?? null,
    },
    {
      key: "contracts",
      label: "Pay contracts",
      count: counts.contracts,
      existing: existing?.contracts ?? null,
    },
  ];
}

export type RerunState = "new" | "partial" | "already";

/** Before confirming: is this a fresh import, a top-up, or a no-op? */
export function previewRerunState(
  counts: ImportCounts,
  existing: ImportExisting | null,
): RerunState {
  if (!existing) return "new";
  const planned = plannedRows(counts);
  const there =
    existing.loads +
    existing.brokers +
    existing.fuel +
    existing.maintenance +
    existing.detentions +
    existing.contracts;
  if (planned > 0 && there >= planned) return "already";
  if (there > 0) return "partial";
  return "new";
}

function plannedRows(c: ImportCounts): number {
  return c.loads + c.brokers + c.fuel + c.maintenance + c.detention + c.contracts;
}

export interface ResultSummary {
  state: RerunState;
  headline: string;
  createdTotal: number;
  skippedTotal: number;
}

/** After persisting: explain what was written vs. already there (idempotent re-run). */
export function summarizeResult(res: LegacyImportResponse): ResultSummary {
  const c = res.created;
  const createdTotal = c.loads + c.brokers + c.expenses + c.detentions + c.contracts;
  const planned = plannedRows(res.counts);
  const skippedTotal = Math.max(0, planned - createdTotal);
  if (planned > 0 && createdTotal === 0) {
    return {
      state: "already",
      headline:
        "Already imported — nothing new was added. Running it again is safe; it never makes duplicates.",
      createdTotal,
      skippedTotal,
    };
  }
  const parts: string[] = [];
  if (c.loads) parts.push(plural(c.loads, "load"));
  if (c.expenses) parts.push(plural(c.expenses, "fuel/maintenance entry", "fuel/maintenance entries"));
  if (c.detentions) parts.push(plural(c.detentions, "detention"));
  if (c.brokers) parts.push(plural(c.brokers, "broker"));
  if (c.contracts) parts.push(plural(c.contracts, "pay contract"));
  const added = parts.length ? `Imported ${parts.join(", ")}.` : "Settings updated.";
  if (skippedTotal > 0) {
    return {
      state: "partial",
      headline: `${added} ${skippedTotal} already there from an earlier import — skipped, no duplicates.`,
      createdTotal,
      skippedTotal,
    };
  }
  return { state: "new", headline: added, createdTotal, skippedTotal };
}

export function plural(n: number, one: string, many?: string): string {
  return `${n} ${n === 1 ? one : (many ?? `${one}s`)}`;
}

export function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

/** Human message for anything that can go wrong talking to /import/legacy. */
export function importErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === "PLAN_READ_ONLY") {
      return "Your plan is read-only until billing is fixed, so nothing can be imported. Settings → Plan.";
    }
    if (err.status === 401) return "You’re signed out. Sign in again, then retry the import.";
    if (err.status === 403) return "Your role can’t import data. Ask the account owner.";
    if (err.status === 413) {
      return "That export is too large to send in one go. Remove old receipt photos or split the load log, then retry.";
    }
    if (err.code === "VALIDATION") return `The server rejected the data: ${err.message}.`;
    if (err.status === 429) return "Too many tries. Wait a minute, then retry.";
    if (err.status >= 500) return "The server hit an error. Nothing was imported; try again in a minute.";
    return err.message || "Import failed.";
  }
  if (err instanceof TypeError) {
    return "Can’t reach Haul Sheet — you look offline. Nothing was imported.";
  }
  return err instanceof Error ? err.message : "Import failed.";
}

export function postLegacyImport(
  bundle: LegacyBundleInput,
  preview: boolean,
  signal?: AbortSignal,
): Promise<LegacyImportResponse> {
  return apiFetch<LegacyImportResponse>("/api/v1/import/legacy", {
    method: "POST",
    body: JSON.stringify({ preview, bundle: slimBundleForUpload(bundle) }),
    signal,
  });
}
